import useDraft from "../hooks/useDraft";
import DraftBanner from "../components/DraftBanner";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { CLASSIFICATIONS, PARTY_KIND_LABELS, newKey, recordsError } from "../lib/records";
import { RecordFilesField } from "../components/records/RecordBits";
import { ClassificationField, PartiesEditor, ReadersPicker, usePeople } from "../components/records/RecordFields";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function Section({ title, hint, children }) {
  return (
    <Card className="p-4 sm:p-5 space-y-4">
      <div>
        <h2 className="font-semibold text-slate-900">{title}</h2>
        {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
      </div>
      {children}
    </Card>
  );
}

function Row({ id, label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

async function pharmacyName(prospectId) {
  for (const path of [`/customers/${prospectId}`, `/prospects/${prospectId}`]) {
    try {
      const { data } = await api.get(path);
      const name = data.pharmacy_name || data.account?.pharmacy_name || data.pharmacy?.name;
      if (name) return name;
    } catch {
      // the reader may not open this file: the server names the pharmacy on save
    }
  }
  return "Pharmacie sélectionnée";
}

export default function RecordFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const people = usePeople();
  const [types, setTypes] = useState([]);
  const [parent, setParent] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [form, setForm] = useState({
    type_code: "", title: "", description: "", effective_date: "", expiry_date: "", notice_days: "", tacit_renewal: false,
    classification: "internal", reader_ids: [], responsible_user_id: user?.id || "", note: "",
  });
  const [parties, setParties] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key] = useState(newKey);
  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));  // A blank form offers the draft kept on this device (never the files); an amendment or a form opened from a pharmacy or a subscription does not.
  const draftValues = useMemo(() => ({ ...form, parties }), [form, parties]);
  const local = useDraft({
    name: "record-new", userId: user?.id, values: draftValues, enabled: !params.get("parent") && !params.get("subscription") && !params.get("prospect"),
    apply: ({ parties: savedParties, ...saved }) => { setForm((f) => ({ ...f, ...saved })); setParties(Array.isArray(savedParties) ? savedParties : []); },
  });

  useEffect(() => { api.get("/records/types").then(({ data }) => setTypes(data.items)).catch(() => setTypes([])); }, []);

  // Where the page was opened from: a pharmacy, a subscription, or the document an amendment changes.
  useEffect(() => {
    const prospect = params.get("prospect");
    const parentId = params.get("parent");
    const subscriptionId = params.get("subscription");
    if (parentId) {
      api.get(`/records/documents/${parentId}`).then(({ data }) => {
        setParent(data);
        setForm((f) => ({ ...f, type_code: data.type_code, title: `Avenant — ${data.title}`, classification: data.classification,
          reader_ids: data.reader_ids || [] }));
        setParties((data.parties || []).map(({ kind, ref, name, role }) => ({ kind, ref, name, role })));
      }).catch(() => setError("Le document à modifier par avenant est introuvable."));
    } else if (subscriptionId) {
      api.get(`/billing/subscriptions/${subscriptionId}`).then(({ data }) => {
        setSubscription({ id: data.id, number: data.number });
        setForm((f) => ({ ...f, type_code: f.type_code || "client_contract", title: f.title || `Contrat — ${data.pharmacy_name}` }));
        setParties([{ kind: "customer", ref: data.prospect_id, name: data.pharmacy_name }, { kind: "company", ref: null, name: "Jamora" }]);
      }).catch(() => {});
    } else if (prospect) {
      pharmacyName(prospect).then((name) => {
        setForm((f) => ({ ...f, type_code: f.type_code || "client_contract" }));
        setParties([{ kind: "customer", ref: prospect, name }, { kind: "company", ref: null, name: "Jamora" }]);
      });
    }
  }, [params]);

  const type = useMemo(() => types.find((t) => t.code === form.type_code), [types, form.type_code]);
  const received = type?.workflow === "received";
  const responsibleOptions = people.filter((p) => form.classification !== "confidential" || p.confidential);
  const canConfidential = hasPermission(user, "records:view_confidential");
  const datesWrong = form.effective_date && form.expiry_date && form.expiry_date < form.effective_date;
  const problems = [
    !form.type_code && "Choisissez le type de document.",
    form.title.trim().length < 2 && "Donnez un titre au document.",
    !parties.length && "Ajoutez au moins une partie.",
    received && !files.length && "Un document reçu s'enregistre avec son fichier.",
    datesWrong && "La date d'expiration précède la date d'effet.",
    form.notice_days !== "" && !form.expiry_date && "Un préavis demande une date d'expiration.",
  ].filter(Boolean);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const body = {
        type_code: form.type_code, title: form.title.trim(), description: form.description.trim(),
        parties: parties.map(({ kind, ref, name, role }) => (kind === "organisation" ? { kind, name, role } : kind === "company" ? { kind } : { kind, ref })),
        responsible_user_id: form.responsible_user_id || null, effective_date: form.effective_date || null, expiry_date: form.expiry_date || null,
        notice_days: form.notice_days === "" ? null : Number(form.notice_days), tacit_renewal: form.tacit_renewal,
        classification: form.classification, reader_ids: form.classification === "restricted" ? form.reader_ids : [],
        subscription_id: subscription?.id || null, parent_id: parent?.id || null, file_ids: files.map((f) => f.id), note: form.note.trim(),
      };
      const { data } = await api.post("/records/documents", body, { headers: { "Idempotency-Key": key } });
      local.clear();
      navigate(`/documents/${data.id}`, { replace: true });
    } catch (e) {
      setError(recordsError(e, "Enregistrement impossible. Réessayez."));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="records-form-page">
      <div className="space-y-1">
        <Link to="/documents" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Documents et contrats</Link>
        <h1 className="font-display font-bold text-2xl text-slate-900">{parent ? `Avenant à ${parent.number}` : "Nouveau document"}</h1>
        <p className="text-sm text-slate-500">
          {parent ? "L'avenant est un nouveau document lié au contrat signé, qui reste tel quel." : "Enregistrez un contrat à faire circuler, ou un document reçu avec son fichier."}
        </p>
      </div>

      <DraftBanner pending={local.pending} onRestore={local.restore} onDismiss={local.dismiss} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <div className="space-y-4">
          <Section title="Le document">
            <div className="grid gap-4 sm:grid-cols-2">
              <Row id="records-type" label="Type">
                <Select value={form.type_code} onValueChange={set("type_code")} disabled={!!parent}>
                  <SelectTrigger id="records-type" className="h-10" data-testid="records-form-type"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </Row>
              <Row id="records-title" label="Titre">
                <Input id="records-title" value={form.title} onChange={(e) => set("title")(e.target.value)} maxLength={200} className="h-10" data-testid="records-form-title" />
              </Row>
            </div>
            <Row id="records-description" label="Description" hint="Facultatif : l'objet du document en quelques lignes.">
              <Textarea id="records-description" value={form.description} onChange={(e) => set("description")(e.target.value)} maxLength={4000} rows={3} />
            </Row>
          </Section>

          <Section title="Parties" hint="Les pharmacies, collaborateurs et organisations concernés. Jamora s'ajoute en un clic.">
            <PartiesEditor parties={parties} setParties={setParties} disabled={!!subscription} />
            {subscription && <p className="text-xs text-slate-500">Lié à l'abonnement {subscription.number} : les parties suivent cet abonnement.</p>}
          </Section>

          <Section title="Dates" hint="L'état « en vigueur » ou « expiré » se calcule chaque jour à partir de ces dates. Une reconduction tacite est signalée, jamais appliquée.">
            <div className="grid gap-4 sm:grid-cols-3">
              <Row id="records-effective" label="Date d'effet">
                <Input id="records-effective" type="date" value={form.effective_date} onChange={(e) => set("effective_date")(e.target.value)} className="h-10" data-testid="records-form-effective" />
              </Row>
              <Row id="records-expiry" label="Date d'expiration">
                <Input id="records-expiry" type="date" value={form.expiry_date} min={form.effective_date || undefined} onChange={(e) => set("expiry_date")(e.target.value)}
                  className="h-10" data-testid="records-form-expiry" />
              </Row>
              <Row id="records-notice" label="Préavis (jours)">
                <Input id="records-notice" type="number" inputMode="numeric" min={0} max={730} value={form.notice_days}
                  onChange={(e) => set("notice_days")(e.target.value)} className="h-10" data-testid="records-form-notice" />
              </Row>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={form.tacit_renewal} onCheckedChange={(v) => set("tacit_renewal")(!!v)} data-testid="records-form-tacit" />Reconduction tacite
            </label>
          </Section>

          <Section title="Accès" hint="Qui voit ce document et son fichier.">
            <ClassificationField value={form.classification} onChange={set("classification")} canConfidential={canConfidential} />
            {form.classification === "restricted" && (
              <ReadersPicker people={people} value={form.reader_ids} onChange={set("reader_ids")} classification={form.classification} />
            )}
            <Row id="records-responsible" label="Responsable" hint="La personne qui suit ce document (son renouvellement, sa résiliation).">
              <Select value={form.responsible_user_id} onValueChange={set("responsible_user_id")}>
                <SelectTrigger id="records-responsible" className="h-10 sm:w-80" data-testid="records-form-responsible"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>{responsibleOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
          </Section>

          <Section title={received ? "Fichier" : "Première version"}
            hint={received ? "Le document reçu est enregistré directement, avec son fichier." : "Facultatif maintenant : le fichier sera nécessaire pour le soumettre en revue."}>
            <RecordFilesField files={files} setFiles={setFiles} required={received} label={received ? "Fichier du document" : "Fichier de la version 1"} />
            {files.length > 0 && (
              <Row id="records-note" label="Note de version">
                <Input id="records-note" value={form.note} onChange={(e) => set("note")(e.target.value)} maxLength={1000} className="h-10" placeholder="Ex. version envoyée au fournisseur" />
              </Row>
            )}
          </Section>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4">
          <Card className="p-4 space-y-3" data-testid="records-form-summary">
            <h2 className="font-semibold text-slate-900">Récapitulatif</h2>
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Type</dt><dd className="text-right">{type?.label || "—"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Parties</dt><dd className="text-right">{parties.length || "—"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Accès</dt><dd className="text-right">{CLASSIFICATIONS[form.classification].label}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Fichiers</dt><dd className="text-right">{files.length}</dd></div>
            </dl>
            <p className="text-xs text-slate-500 border-t border-slate-100 pt-3">
              {type ? (received ? "Document reçu : enregistré tel quel, sans circuit d'approbation." : "Circuit : brouillon, revue par une autre personne, approbation, puis signature consignée avec la copie signée.") : "Le type décide du circuit du document."}
            </p>
            {parties.some((p) => p.kind !== "company") && (
              <p className="text-xs text-slate-500">{parties.filter((p) => p.kind !== "company").map((p) => `${PARTY_KIND_LABELS[p.kind]} : ${p.name}`).join(" · ")}</p>
            )}
          </Card>
          {problems.length > 0 && (
            <ul className="space-y-1 text-xs text-slate-500" data-testid="records-form-problems">{problems.map((p) => <li key={p}>• {p}</li>)}</ul>
          )}
          {error && <p className="text-sm text-red-600" role="alert" data-testid="records-form-error">{error}</p>}
          <div className="flex gap-2">
            <Button variant="outline" asChild className="flex-1"><Link to="/documents">Annuler</Link></Button>
            <Button className="flex-1" onClick={submit} disabled={busy || problems.length > 0} data-testid="records-form-submit">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
